import { useCallback, useEffect, useState } from 'react'
import { ErroreApi } from '../../servizi/api.ts'
import { Attesa, Avviso, Vuoto } from '../../componenti/Pezzi.tsx'
import { AreaTesto, Bottone, Campo, Modale, Tabella } from '../../componenti/base.tsx'
import * as posta from '../../servizi/posta.ts'

/**
 * Inoltri in deroga (536, decisione di Davide del 2/11/2026): la posta in arrivo per una casella
 * ospitata va anche a un indirizzo fuori CIDIS.
 *
 *  - `ConcediInoltro`: dalla riga della casella in «Caselle e ricezione», per chi gestisce le utenze.
 *    Indirizzo, motivo obbligatorio e l'avvertenza privacy: la posta esce da CIDIS, la copia nel
 *    gestionale resta.
 *  - `InoltriInDeroga`: l'elenco delle deroghe attive con chi le ha concesse e il motivo, e «Revoca»
 *    in un clic. Lo vedono chi gestisce le utenze e la Referente privacy (o il DPO,
 *    `posta.inoltri_controlla`), che riceve un avviso a ogni concessione e revoca. La lettura resta nel
 *    registro.
 * Le regole (una sola deroga attiva per casella, indirizzo fuori dai domini di CIDIS) le applica l'API.
 */

const testoErrore = (e: unknown) =>
  (e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.')

const giorno = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })

export function ConcediInoltro({ casella, chiudi, concesso }: {
  casella: { id: string; indirizzo: string }; chiudi: () => void; concesso: (testo: string) => void
}) {
  const [destinazione, setDestinazione] = useState('')
  const [motivo, setMotivo] = useState('')
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  async function concedi() {
    setInCorso(true); setErrore(null)
    try {
      const r = await posta.concediInoltro(casella.id, destinazione, motivo)
      concesso(`La posta da fuori CIDIS per ${r.casella} va anche a ${r.destinazione}; per quella interna parte un avviso. `
        + 'La Referente privacy ha ricevuto un avviso.')
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <Modale titolo={`Inoltro in deroga di ${casella.indirizzo}`} chiudi={chiudi}
      azioni={
        <>
          <Bottone variante="discreto" onClick={chiudi}>Annulla</Bottone>
          <Bottone variante="primario" inCorso={inCorso}
                   disabled={!destinazione.trim() || motivo.trim().length < 10} onClick={() => void concedi()}>
            Concedi l’inoltro
          </Bottone>
        </>
      }>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      <Avviso tipo="attenzione">
        È una deroga alla regola: la posta di CIDIS resta nel gestionale. Con l’inoltro ogni messaggio in
        arrivo per questa casella esce da CIDIS e finisce su un servizio che CIDIS non controlla, con i dati
        personali che contiene. Concedilo solo con un motivo di lavoro, verso un indirizzo della persona
        che usa la casella.
      </Avviso>
      <ul className="piccolo">
        <li>si inoltra solo la posta che arriva da fuori CIDIS, com’è, con il mittente vero: «Rispondi» va a chi ha scritto;</li>
        <li>la posta interna (colleghi, caselle @esterni, email automatiche) non si inoltra: all’indirizzo arriva solo
          un avviso con quante sono e il collegamento, al più uno ogni 10 minuti;</li>
        <li>la copia nel gestionale resta sempre;</li>
        <li>la posta indesiderata non si inoltra;</li>
        <li>la concessione resta nel registro eventi e la Referente privacy riceve un avviso; si revoca in un clic.</li>
      </ul>
      <Campo tipo="email" etichetta="Indirizzo a cui inoltrare" valore={destinazione} cambia={setDestinazione}
             obbligatorio autoComplete="off" aiuto="Fuori da CIDIS: per un collega si usa una casella di gruppo." />
      <AreaTesto etichetta="Motivo della deroga" valore={motivo} cambia={setMotivo} righe={3} obbligatorio
                 maxLength={2000} aiuto="Resta nel registro e lo legge la Referente privacy. Almeno 10 caratteri." />
    </Modale>
  )
}

export function InoltriInDeroga({ aggiornato = 0, revocato }: {
  /** Cambia quando la pagina intorno concede o revoca: l'elenco si rilegge. */
  aggiornato?: number
  revocato?: () => void
}) {
  const [elenco, setElenco] = useState<posta.InoltroInDeroga[] | null>(null)
  const [daRevocare, setDaRevocare] = useState<posta.InoltroInDeroga | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)

  const carica = useCallback(() => {
    posta.elencaInoltri().then(setElenco).catch((e) => setErrore(testoErrore(e)))
  }, [])
  useEffect(() => { carica() }, [carica, aggiornato])

  async function revoca() {
    if (!daRevocare) return
    setInCorso(true); setErrore(null)
    try {
      await posta.revocaInoltro(daRevocare.id)
      setDaRevocare(null)
      carica()
      revocato?.()
    } catch (e) { setErrore(testoErrore(e)) } finally { setInCorso(false) }
  }

  return (
    <div>
      <div className="posta-titoletto">Inoltri in deroga</div>
      <p className="piccolo tenue">
        Caselle la cui posta in arrivo da fuori CIDIS va anche a un indirizzo esterno; per la posta interna parte
        solo un avviso. La copia nel gestionale resta; la revoca ferma inoltro e avvisi entro qualche minuto.
        Questa lettura resta nel registro eventi.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!elenco && !errore && <Attesa testo="Caricamento…" />}
      {elenco && (
        <Tabella intestazioni={['Casella', 'Verso', 'Motivo', 'Concesso',
                                <span className="solo-lettore" key="azioni">Azioni</span>]}
                 vuoto={<Vuoto testo="Nessun inoltro in deroga." />}>
          {elenco.map((i) => (
            <tr key={i.id}>
              <td data-etichetta="Casella">
                <div className="voce-principale">{i.casella}</div>
                {i.titolare && <div className="piccolo tenue">{i.titolare.cognome} {i.titolare.nome}</div>}
              </td>
              <td data-etichetta="Verso" className="piccolo">{i.destinazione}</td>
              <td data-etichetta="Motivo" className="piccolo">«{i.motivo}»</td>
              <td data-etichetta="Concesso" className="piccolo">
                {giorno.format(new Date(i.concessa_il))}
                <div className="tenue">da {i.concessa_da.nome} {i.concessa_da.cognome}</div>
                {i.avvisi_inviati > 0 && (
                  <div className="tenue">
                    {i.avvisi_inviati === 1 ? '1 avviso' : `${i.avvisi_inviati} avvisi`} della posta interna
                    ({i.email_avvisate === 1 ? '1 email' : `${i.email_avvisate} email`})
                  </div>
                )}
              </td>
              <td data-etichetta="Azioni">
                <Bottone dimensione="piccolo" variante="discreto" aria-label={`Revoca l’inoltro di ${i.casella}`}
                         onClick={() => { setErrore(null); setDaRevocare(i) }}>
                  Revoca
                </Bottone>
              </td>
            </tr>
          ))}
        </Tabella>
      )}
      {daRevocare && (
        <Modale titolo="Revoca l’inoltro" chiudi={() => setDaRevocare(null)} fuocoIniziale="azioni"
          azioni={
            <>
              <Bottone variante="discreto" onClick={() => setDaRevocare(null)}>Annulla</Bottone>
              <Bottone variante="critico" inCorso={inCorso} onClick={() => void revoca()}>Revoca</Bottone>
            </>
          }>
          {errore && <Avviso tipo="errore">{errore}</Avviso>}
          <p>
            La posta in arrivo per {daRevocare.casella} non andrà più a {daRevocare.destinazione}. La copia
            nel gestionale resta com’è. La revoca resta nel registro e la Referente privacy riceve un avviso.
          </p>
        </Modale>
      )}
    </div>
  )
}
